// Apple Pay on the web (CHIR-6954): shows the Apple Pay button and runs merchant validation.
// Taking the payment itself (token -> tokenization -> auth -> capture) comes in later stories. Used by the cart
// (addApplePayButton) and the checkout (CHIR-6950, via start() with the summary rows).

// Version 3: supported by every Apple Pay-capable Safari we target; has all this flow needs.
const APPLE_PAY_VERSION = 3;

const sdkLoads = new Map();

// Apple's SDK provides <apple-pay-button> and Apple Pay in Chrome/Edge/Firefox (QR to iPhone).
// The URL comes from config (ApplePaySdkUrl) so an environment can pin a specific version.
function loadAppleSdk(url) {
  if (!url) {
    return Promise.reject(new Error('Apple Pay JS SDK URL (ApplePaySdkUrl) is not configured'));
  }
  if (!sdkLoads.has(url)) {
    sdkLoads.set(
      url,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.crossOrigin = 'anonymous';
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener(
          'error',
          () => {
            // Forget the failure so the next cart re-render tries again.
            sdkLoads.delete(url);
            script.remove();
            reject(new Error(`Apple Pay JS SDK failed to load from ${url}`));
          },
          { once: true }
        );
        document.head.appendChild(script);
      })
    );
  }
  return sdkLoads.get(url);
}

// Per page: Apple's availability answer (so cart re-renders show the button without waiting
// on Apple again), the one session Apple allows at a time, and which sessions were cancelled.
const pageStates = new WeakMap();
function pageState(win) {
  if (!pageStates.has(win)) {
    pageStates.set(win, { availability: new Map(), activeSession: null, cancelled: new WeakSet() });
  }
  return pageStates.get(win);
}

// Reports to New Relic; never throws, so telemetry can't break the payment flow or the cart render.
function reportError(win, error, attributes) {
  try {
    const { newrelic } = win || {};
    if (newrelic) {
      newrelic.noticeError(error, attributes);
    }
  } catch (e) {
    // Nothing more we can do without breaking the page.
  }
}

export default class ApplePay {
  constructor({
    merchantIdentifier,
    label,
    validateMerchantUrl,
    sdkUrl,
    placement,
    onFailure,
    window: win = window,
    fetch: fetchFn = (...args) => window.fetch(...args),
    loadSdk,
  } = {}) {
    this.merchantIdentifier = merchantIdentifier;
    this.label = label;
    this.validateMerchantUrl = validateMerchantUrl;
    // Which page this runs on ('cart' or 'checkout'), added to every New Relic event so the two can be told apart.
    this.placement = placement;
    // Lets the page show its own message when the sheet can't start or merchant validation fails (not on a cancel).
    this.onFailure = onFailure;
    this.window = win;
    this.fetch = fetchFn;
    this.loadSdk = loadSdk || (() => loadAppleSdk(sdkUrl));
  }

  withPlacement(attributes) {
    return this.placement ? Object.assign({}, attributes, { applePayPlacement: this.placement }) : attributes;
  }

  // Never throws: a bug in the page's handler must not break the Apple Pay flow.
  notifyFailure() {
    if (typeof this.onFailure !== 'function') {
      return;
    }
    try {
      this.onFailure();
    } catch (e) {
      // The page's own problem, reported so it isn't silent; the session has already been closed and released.
      this.logError(e, 'notifyFailure');
    }
  }

  // Errors go to New Relic like the other payment code (OrangePay); everything else is an
  // info-level page action. Nothing about the shopper is sent. No-op where New Relic isn't loaded.
  logError(error, step, attributes = {}) {
    reportError(this.window, error, this.withPlacement(Object.assign({ applePayStep: step }, attributes)));
  }

  logInfo(step) {
    try {
      const { newrelic } = this.window || {};
      if (newrelic) {
        newrelic.addPageAction('applePay', this.withPlacement({ applePayStep: step }));
      }
    } catch (e) {
      // Telemetry must never break the payment flow.
    }
  }

  isAvailable() {
    const { availability } = pageState(this.window);
    if (!availability.has(this.merchantIdentifier)) {
      availability.set(this.merchantIdentifier, this.checkAvailability());
    }
    return availability.get(this.merchantIdentifier);
  }

  async checkAvailability() {
    try {
      await this.loadSdk();
    } catch (e) {
      // Not remembered: a failed script load is retried on the next mount.
      pageState(this.window).availability.delete(this.merchantIdentifier);
      this.logError(e instanceof Error ? e : new Error('Apple Pay JS SDK failed to load'), 'loadSdk');
      return false;
    }

    const session = this.window.ApplePaySession;
    if (!session) {
      return this.unavailable();
    }

    // Apple's guidelines: prefer applePayCapabilities; hide the button only when unsupported.
    if (typeof session.applePayCapabilities === 'function') {
      try {
        const capabilities = await session.applePayCapabilities(this.merchantIdentifier);
        return capabilities.paymentCredentialStatus !== 'applePayUnsupported' || this.unavailable();
      } catch (e) {
        this.logError(e, 'checkAvailability');
        return false;
      }
    }

    // Apple throws InvalidAccessError on non-HTTPS pages rather than returning false.
    try {
      return Boolean(session.canMakePayments()) || this.unavailable();
    } catch (e) {
      this.logError(e, 'checkAvailability');
      return false;
    }
  }

  // Apple said no (e.g. an unsupported browser, or a domain Apple hasn't verified): info, not an error. Only testers
  // reach this while the tester flag is required; at go-live it would be one event per unsupported visitor.
  unavailable() {
    this.logInfo('unavailable');
    return false;
  }

  // Renders the button into `container` (which carries data-amount) and shows it,
  // only if Apple Pay is available. Mutating the container is the point of this method.
  /* eslint-disable no-param-reassign */
  async mount(container) {
    // Apple rejects a zero total, so a $0 cart gets no button.
    if (!container || !(Number(container.getAttribute('data-amount')) > 0)) {
      return;
    }
    if (!(await this.isAvailable())) {
      return;
    }

    const button = document.createElement('apple-pay-button');
    button.setAttribute('buttonstyle', 'black');
    button.setAttribute('type', 'check-out');
    button.setAttribute('locale', 'en-US');
    button.addEventListener('click', () => this.start(container.getAttribute('data-amount')));

    container.innerHTML = '';
    container.appendChild(button);
    container.hidden = false;
    this.logInfo('buttonShown');
  }
  /* eslint-enable no-param-reassign */

  // Must run synchronously inside the click: Safari refuses to create a session after an await.
  start(payment) {
    const state = pageState(this.window);
    if (state.activeSession) {
      // Double click/tap: Apple allows one session per page and throws on a second.
      return state.activeSession;
    }

    const { ApplePaySession } = this.window;
    // The cart passes the total as a string; the checkout passes { total, lineItems } (the page summary's rows).
    const isSummary = payment !== null && typeof payment === 'object';
    const request = {
      countryCode: 'US',
      currencyCode: 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      total: { label: this.label, amount: isSummary ? payment.total : payment },
    };
    if (isSummary && Array.isArray(payment.lineItems) && payment.lineItems.length > 0) {
      request.lineItems = payment.lineItems.map(({ label, amount }) => ({ label, amount, type: 'final' }));
    }

    let session;
    try {
      session = new ApplePaySession(APPLE_PAY_VERSION, request);
    } catch (e) {
      // e.g. a request Apple rejects; the rest of the page keeps working.
      this.logError(e, 'startSession');
      this.notifyFailure();
      return null;
    }

    session.onvalidatemerchant = (event) => this.validateMerchant(session, event.validationURL);

    // No payment in this story: close the sheet without charging (CHIR-6953+ sends the token on).
    session.onpaymentauthorized = () => {
      this.logInfo('paymentAuthorized');
      session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
      this.release(session);
    };

    session.oncancel = () => {
      // Our own abort() (validateMerchant) also lands here in Chrome/Edge/Firefox: only the shopper's cancel counts.
      if (!state.cancelled.has(session)) {
        state.cancelled.add(session);
        this.logInfo('cancelled');
      }
      this.release(session);
    };

    state.activeSession = session;
    try {
      session.begin();
    } catch (e) {
      this.logError(e, 'startSession');
      this.release(session);
      this.notifyFailure();
      return null;
    }
    this.logInfo('sessionStarted');
    return session;
  }

  release(session) {
    const state = pageState(this.window);
    if (state.activeSession === session) {
      state.activeSession = null;
    }
  }

  async validateMerchant(session, validationUrl) {
    const { cancelled } = pageState(this.window);
    let httpStatus;
    try {
      const response = await this.fetch(this.validateMerchantUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ validationUrl }),
      });
      httpStatus = response.status;
      if (!response.ok) {
        throw new Error(`Apple Pay merchant validation failed (${response.status})`);
      }
      const merchantSession = await response.json();
      // The shopper cancelled while we waited: leave that session alone. In Chrome/Edge/Firefox Apple's SDK shares
      // one pop-up per page, so touching an old session could close the shopper's next attempt.
      if (cancelled.has(session)) {
        return;
      }
      session.completeMerchantValidation(merchantSession);
      this.logInfo('merchantValidated');
    } catch (e) {
      // Cancelled by the shopper: not an error, and nothing to close (see above).
      if (cancelled.has(session)) {
        return;
      }
      // The server logged why it failed; this adds the browser's side (e.g. the request never reached us).
      this.logError(e, 'validateMerchant', httpStatus ? { applePayHttpStatus: httpStatus } : {});
      // Marked first: in Chrome/Edge/Firefox abort() fires oncancel, and closing it ourselves isn't a shopper cancel.
      cancelled.add(session);
      // Closing the sheet leaves the cart and other payment options untouched.
      try {
        session.abort();
      } catch (alreadyClosed) {
        // Apple had already closed the sheet.
      }
      this.release(session);
      this.notifyFailure();
    }
  }
}

// Called after every cart render (first load and every re-render). The server only renders the placeholder when
// Apple Pay is on for this visitor (the admin setting AND the tester flag), so its absence means nothing runs.
// Apple Pay problems are reported, never thrown: the cart's other buttons and the rest of the render run after this.
export function addApplePayButton({
  globals: pageGlobals = {},
  window: win = window,
  create = (options) => new ApplePay(options),
} = {}) {
  const container = document.querySelector('#gcc-cart-applepay');
  if (!container) {
    return;
  }
  try {
    const config = pageGlobals.applePay || {};
    const applePay = create({
      merchantIdentifier: config.merchantIdentifier,
      label: config.label,
      sdkUrl: config.sdkUrl,
      validateMerchantUrl: (pageGlobals.paths || {}).validateApplePayMerchant,
      placement: 'cart',
      window: win,
    });
    applePay.mount(container).catch((e) => reportError(win, e, { applePayStep: 'mount', applePayPlacement: 'cart' }));
  } catch (e) {
    reportError(win, e, { applePayStep: 'mount', applePayPlacement: 'cart' });
  }
}
